import { ChangeDetectorRef, Component, Input } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Subject } from 'rxjs';
import { takeUntil } from 'rxjs';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { filterBoolean } from '@core/common-legacy';
import { StatusChangeType } from '@core/offers-data';
import { OfferStatus } from '@core/offers-types';
import type { OfferSimpleListItem } from '@core/offers-ui';
import { LifeCycle } from '@core/utils-legacy';
import { isRequisitionRequired$ } from '@recruitee/offers-util';

import { OfferActionsFacade } from '../facades/offer-actions-facade.types';
import { StatusChangeFacade } from '../facades/status-change-facade.types';
import { StatusesDropdownComponent } from '../offers-page/statuses-dropdown/statuses-dropdown.component';

@Component({
  selector: 'rt-statuses-dropdown-container',
  imports: [StatusesDropdownComponent],
  template: `
    <rt-statuses-dropdown
      [offer]="offer"
      [state]="state"
      [hasPublishActions]="hasPublishActions"
      [isPlain]="isPlain"
      (action)="action($event)"
    ></rt-statuses-dropdown>
  `,
})
export class StatusesDropdownContainerComponent {
  @Input() public offer: OfferSimpleListItem;
  @Input() public isPlain: boolean = true;

  public abilities = {
    manageJobs: false,
    publishJobs: false,
  };
  public isRequisitionRequired = false;

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private statusChangeFacade: StatusChangeFacade,
    private actionsFacade: OfferActionsFacade,
    private store: Store,
    private cdRef: ChangeDetectorRef,
  ) {}

  public get hasPublishActions() {
    return this.abilities.publishJobs && this.offer.status !== OfferStatus.Archived;
  }

  public get state() {
    if (!this.abilities.manageJobs) return 'read-only';
    return 'default';
  }

  public ngOnInit() {
    isRequisitionRequired$(this.store)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(isRequired => {
        this.isRequisitionRequired = isRequired;
        this.cdRef.detectChanges();
      });

    this.store
      .select(AbilitiesSelectors.abilityMap)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(abilities => {
        this.abilities = {
          manageJobs: abilities[Ability.ManageJobs],
          publishJobs: abilities[Ability.PublishJobs],
        };
        this.cdRef.detectChanges();
      });
  }

  public action(name: StatusChangeType) {
    switch (name) {
      case StatusChangeType.Publish:
        this.statusChangeFacade.setPublished(this.offer);
        break;
      case StatusChangeType.Unpublish:
        this.statusChangeFacade.setInternal(this.offer);
        break;
      case StatusChangeType.Close:
        this.statusChangeFacade.setClosed(this.offer);
        break;
      case StatusChangeType.Archive:
        this.actionsFacade.archive(this.offer);
        break;
      default:
        break;
    }
  }
}
